import { useInterval, useTimeout } from '@solinkcorp/solink-components';
import { Button } from '@solinkcorp/solink-ui/components/Button';
import { Dialog, DialogContent, DialogTitle } from '@solinkcorp/solink-ui/components/Dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@solinkcorp/solink-ui/components/DropdownMenu';
import { showToast } from '@solinkcorp/solink-ui/components/Toast';
import { useWindowVisibility } from '@solinkcorp/utils/hooks';
import { CommonContext } from 'components/Contexts';
import { ChevronDownIcon } from 'lucide-react';
import { useCallback, useContext, useEffect, useState } from 'react';
import CountUp from 'react-countup';
import useLocale from 'src/hooks/useLocale';
import { getBuildInfo } from 'utils/BuildInfoUtil';
import { MINUTES_IN_MILLISECONDS } from 'vd-constants';
import pkg from '../../../package.json';

const { version } = pkg;

/** Sonner toast id. A stable id lets us update or dismiss the same toast. */
const RELOAD_TOAST_ID = 'app-reload-notifier';
/** Time from first detection to the forced reload deadline. */
const INITIAL_COUNTDOWN_MS = 5 * MINUTES_IN_MILLISECONDS;
/** Length of the final countdown modal. */
const MODAL_COUNTDOWN_SECONDS = 30;
const MODAL_COUNTDOWN_MS = MODAL_COUNTDOWN_SECONDS * 1000;
/** Postpone durations, in minutes, offered in the "Reload in..." menu. */
const REMINDER_OPTIONS_MINUTES = [15, 30, 60, 360];
/** After a postpone, the toast returns this long before the new deadline. */
const TOAST_REMINDER_LEAD_MS = 5 * MINUTES_IN_MILLISECONDS;

function getMMSSFromSeconds(seconds: number) {
  return new Date(seconds * 1000).toISOString().slice(15, 19);
}

/**
 * The menu sits on `document.body`, outside the countdown dialog and outside
 * the toast list.
 *
 * Hovering an item moves focus onto the menu. The parent then pulls focus
 * back, and the menu reads that move as a dismiss. Block the hover focus, and
 * block the dismiss, so the menu stays open.
 */
function keepReloadMenuOpen(event: { preventDefault: () => void }) {
  event.preventDefault();
}

/** Label a postpone option: whole-hour values read as "6 hours", else "30 minutes". */
function formatReminderLabel(minutes: number, minutesLabel: string, hoursLabel: string) {
  return minutes >= 120 && minutes % 60 === 0
    ? `${minutes / 60} ${hoursLabel}`
    : `${minutes} ${minutesLabel}`;
}

interface ReloadControlsProps {
  isCritical: boolean;
  onReloadNow: () => void;
  onPostpone: (delayMs: number) => void;
}

/**
 * Shared action controls: "Reload now" plus the "Reload in..." postpone menu.
 * The postpone menu is hidden for critical releases.
 */
function ReloadControls({ isCritical, onReloadNow, onPostpone }: ReloadControlsProps) {
  const locale = useLocale();

  return (
    <div className="tw flex items-center gap-2">
      <Button size="sm" onClick={onReloadNow}>
        {locale.reloadNotice.reloadNow}
      </Button>
      {!isCritical && (
        // Non-modal so this menu (rendered inside the modal countdown Dialog)
        // does not add its own `pointer-events: none` body lock that can stick
        // when the Dialog closes on select.
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button size="sm">
              {locale.reloadNotice.reloadIn + '...'}
              <ChevronDownIcon className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          {/* Lift above the reload toast and the countdown dialog so the
              portaled menu is not rendered behind them. */}
          <DropdownMenuContent
            style={{ zIndex: 1_000_000_000 }}
            onPointerMoveCapture={keepReloadMenuOpen}
            onFocusOutside={keepReloadMenuOpen}
          >
            {REMINDER_OPTIONS_MINUTES.map((minutes) => (
              <DropdownMenuItem
                key={minutes}
                onSelect={() => onPostpone(MINUTES_IN_MILLISECONDS * minutes)}
              >
                {formatReminderLabel(minutes, locale.general.minutes, locale.general.hours)}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}

/** Shared classes for the toast action buttons — the design-system Toast action
 * button style (transparent with inverse border and text) for the colored info
 * surface. */
const TOAST_ACTION_BUTTON =
  'tw inline-flex max-h-12 min-h-8 items-center justify-center gap-1 whitespace-nowrap rounded-lg border border-solid border-text-inverse bg-transparent px-3 py-1 outline-none text-text-inverse label-sm';

/** Locale strings the toast needs. Resolved by the caller with the app locale. */
interface ReloadToastLabels {
  reloadNow: string;
  reloadIn: string;
  minutes: string;
  hours: string;
}

/**
 * The toast action cluster: "Reload now" beside the "Reload in..." postpone
 * menu. The postpone menu is hidden for critical releases.
 *
 * Labels are passed in (resolved with the app locale by the caller) rather than
 * read with `useLocale`, because this renders inside the sonner Toaster, which
 * is outside the app providers — `useLocale` there falls back to the browser
 * locale, not the language chosen in Settings.
 */
function ReloadToastActions({
  isCritical,
  onReloadNow,
  onPostpone,
  labels,
}: ReloadControlsProps & { labels: ReloadToastLabels }) {
  return (
    <>
      <button type="button" className={TOAST_ACTION_BUTTON} onClick={onReloadNow}>
        {labels.reloadNow}
      </button>
      {!isCritical && (
        // Non-modal so dismissing the toast on postpone cannot leave a stuck
        // `pointer-events: none` body lock from the menu's close animation.
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <button type="button" className={TOAST_ACTION_BUTTON}>
              {labels.reloadIn + '...'}
              <ChevronDownIcon className="size-4" />
            </button>
          </DropdownMenuTrigger>
          {/* Lift above the reload toast and the countdown dialog so the
              portaled menu is not rendered behind them. */}
          <DropdownMenuContent
            style={{ zIndex: 1_000_000_000 }}
            onPointerMoveCapture={keepReloadMenuOpen}
            onFocusOutside={keepReloadMenuOpen}
          >
            {REMINDER_OPTIONS_MINUTES.map((minutes) => (
              <DropdownMenuItem
                key={minutes}
                onSelect={() => onPostpone(MINUTES_IN_MILLISECONDS * minutes)}
              >
                {formatReminderLabel(minutes, labels.minutes, labels.hours)}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </>
  );
}

interface ReloadToastParams extends ReloadControlsProps {
  reloadTimestamp: number;
  title: string;
  countUpFormatter: (secondsRemaining: number) => string;
  labels: ReloadToastLabels;
}

function showReloadToast({
  reloadTimestamp,
  title,
  countUpFormatter,
  isCritical,
  onReloadNow,
  onPostpone,
  labels,
}: ReloadToastParams) {
  const startTimeSeconds = Math.max(0, (reloadTimestamp - Date.now()) / 1000);

  showToast.info(
    {
      title,
      closeButton: false,
      className: 'w-[min(92vw,520px)]',
      description: (
        // tabular-nums keeps the ticking digits a fixed width so the toast does
        // not reflow (and sonner does not reposition it) each second.
        <span className="tabular-nums">
          <CountUp
            end={0}
            start={startTimeSeconds}
            duration={startTimeSeconds}
            useEasing={false}
            formattingFn={countUpFormatter}
          />
        </span>
      ),
      action: (
        <ReloadToastActions
          isCritical={isCritical}
          onReloadNow={onReloadNow}
          onPostpone={onPostpone}
          labels={labels}
        />
      ),
    },
    {
      id: RELOAD_TOAST_ID,
      duration: Infinity,
      position: 'bottom-center',
      dismissible: false,
      // Force auto height on the sonner element so its hover/expand rule
      // (`height: var(--initial-height)`) cannot snap the toast to a stale
      // height measured before web fonts settle. Without this the first toast
      // jumps ~10px on hover and can flip-flop in a hover feedback loop.
      // Scoped here rather than in `showToast` because it overrides sonner's
      // collapsed-stack height; safe for this single persistent toast, but it
      // would break the look of multiple stacked toasts.
      style: { height: 'auto' },
    },
  );
}

/**
 * Owns the reload state and drives both the sonner toast and the final
 * countdown modal. The toast replaces the old reload banner so the update
 * notice stays visible in every view, including the full-screen event player.
 */
function ReloadNotifier({
  isReloadCritical,
  initialCountdownMs,
}: {
  isReloadCritical: boolean;
  initialCountdownMs: number;
}) {
  const locale = useLocale();
  const { appState } = useContext(CommonContext);
  const setReloadNoticeDeadline = appState.setReloadNoticeDeadline;
  const [reloadTimestamp, setReloadTimestamp] = useState<number>(
    () => Date.now() + initialCountdownMs,
  );
  // Open the modal straight away when the deadline is already within the modal
  // window (used by the developer "Show countdown modal" test).
  const [showReloadModal, setShowReloadModal] = useState(
    () => initialCountdownMs <= MODAL_COUNTDOWN_MS,
  );
  // Hidden after a postpone; returns shortly before the new deadline.
  const [isToastVisible, setIsToastVisible] = useState(true);

  // Publish the current deadline so the developer test panel can show the
  // remaining deferred time. Clear it when the notifier unmounts.
  useEffect(() => {
    setReloadNoticeDeadline(reloadTimestamp);
  }, [reloadTimestamp, setReloadNoticeDeadline]);
  useEffect(() => () => setReloadNoticeDeadline(null), [setReloadNoticeDeadline]);

  // Full message for the countdown modal.
  const countUpFormatter = useCallback(
    (secondsRemaining: number) =>
      `${locale.reloadNotice.newAppVersionAvailable} ` + getMMSSFromSeconds(secondsRemaining),
    [locale],
  );

  // Short "Reloading in MM:SS" used as the toast description (the title carries
  // the message).
  const toastCountdownFormatter = useCallback(
    (secondsRemaining: number) =>
      `${locale.reloadNotice.reloadingIn} ${getMMSSFromSeconds(secondsRemaining)}`,
    [locale],
  );

  const onReloadNow = useCallback(() => {
    location.reload();
  }, []);

  const onPostpone = useCallback((delayMs: number) => {
    setReloadTimestamp(Date.now() + delayMs);
    setShowReloadModal(false);
    // Give the user quiet time; the toast returns near the new deadline.
    setIsToastVisible(false);
  }, []);

  // Open the final countdown modal MODAL_COUNTDOWN_SECONDS before the deadline.
  useTimeout(
    () => {
      setShowReloadModal(true);
    },
    Math.max(0, reloadTimestamp - MODAL_COUNTDOWN_MS - Date.now()),
    [reloadTimestamp],
  );

  // Bring the toast back shortly before the deadline after a postpone.
  useTimeout(
    () => {
      setIsToastVisible(true);
    },
    Math.max(0, reloadTimestamp - TOAST_REMINDER_LEAD_MS - Date.now()),
    [reloadTimestamp],
  );

  // Show or refresh the toast whenever the deadline or controls change.
  // Dismiss it while the modal is open, or during the quiet period after a
  // postpone, so only one countdown is visible at a time.
  useEffect(() => {
    if (showReloadModal || !isToastVisible) {
      showToast.dismiss(RELOAD_TOAST_ID);
      return;
    }

    showReloadToast({
      reloadTimestamp,
      title: locale.reloadNotice.newAppVersionAvailableOnly,
      countUpFormatter: toastCountdownFormatter,
      isCritical: isReloadCritical,
      onReloadNow,
      onPostpone,
      labels: {
        reloadNow: locale.reloadNotice.reloadNow,
        reloadIn: locale.reloadNotice.reloadIn,
        minutes: locale.general.minutes,
        hours: locale.general.hours,
      },
    });
  }, [
    showReloadModal,
    isToastVisible,
    reloadTimestamp,
    isReloadCritical,
    onReloadNow,
    onPostpone,
    toastCountdownFormatter,
    locale,
  ]);

  // Dismiss the toast if this notifier unmounts.
  useEffect(() => {
    return () => {
      showToast.dismiss(RELOAD_TOAST_ID);
    };
  }, []);

  // Edge case: the app was in a background tab when the timer expired.
  const didUserReenterApp = useWindowVisibility();
  if (didUserReenterApp && Date.now() > reloadTimestamp && !showReloadModal) {
    setShowReloadModal(true);
  }

  return (
    <Dialog open={showReloadModal}>
      <DialogContent
        showCloseButton={false}
        // Lift above the full-screen event player and other solink-components
        // modals, which stack from z 10000 upward via PopoverZIndexManager.
        size="xs"
        className="z-[100000]"
        overlayClassName="z-[100000]"
        // Forced countdown: the user cannot dismiss it, only reload or postpone.
        onEscapeKeyDown={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
      >
        <DialogTitle className="sr-only">
          {locale.reloadNotice.newAppVersionAvailableOnly}
        </DialogTitle>
        <div className="flex flex-col items-center gap-6 text-center">
          {/* Only mount the countdown while the dialog is open. Radix keeps
              DialogContent mounted through its exit animation, so without this
              guard a postpone in the final moment would let CountUp reach 0 and
              reload anyway. Unmounting cancels the timer without firing onEnd. */}
          {showReloadModal && (
            <div className="heading-md">
              <CountUp
                end={0}
                start={MODAL_COUNTDOWN_SECONDS}
                duration={MODAL_COUNTDOWN_SECONDS}
                useEasing={false}
                formattingFn={countUpFormatter}
                onEnd={onReloadNow}
              />
            </div>
          )}
          <ReloadControls
            isCritical={isReloadCritical}
            onReloadNow={onReloadNow}
            onPostpone={onPostpone}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ReloadNotice() {
  const { appState } = useContext(CommonContext);

  const reloadNotice = appState.reloadNotice;

  useInterval(
    () => {
      if (reloadNotice !== null) return;
      getBuildInfo().then((info) => {
        if (info && info.version !== version) {
          appState.setReloadNotice(info.criticalRelease ? 'critical' : 'non-critical');
        }
      });
    },
    10 * MINUTES_IN_MILLISECONDS,
    [reloadNotice],
  );

  if (appState.reloadNotice === null) return null;

  const isReloadCritical = appState.reloadNotice === 'critical';
  const initialCountdownMs = appState.reloadNoticeCountdownMs ?? INITIAL_COUNTDOWN_MS;

  return (
    // Remount on each trigger so a new deadline (for example a developer test)
    // restarts the countdown from its initializer.
    <ReloadNotifier
      key={`${appState.reloadNotice}:${appState.reloadNoticeCountdownMs ?? 'default'}`}
      isReloadCritical={isReloadCritical}
      initialCountdownMs={initialCountdownMs}
    />
  );
}

export default ReloadNotice;
